import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Header } from "./Header";
import { AspectRatioSelector } from "./AspectRatioSelector";
import { CropperSection } from "./CropperSection";
import { getComputedAspect } from "./CropperHelper";
import { Container, Sidebar } from "../../components/ImageCropModal/ModalCrop.style";
import { FlexContainer, Toast } from "src/components";
import { AssetList } from "../CarouselCrop/AssetList";

// Sets screen width and height for mobile view,
const useStyles = makeStyles((theme) => ({
    paper: {
        width: 1425,
        height: 885,
        borderRadius: 15,
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            height: "100vh",
            width: "100%",
            maxWidth: "100%",
            borderRadius: 0,
        },
    },
}));

export default function VideoCrop({
    videoSrc,
    isOpen,
    onClose,
    onCropComplete,
    aspectRatios,
    allowedOrientations,
    carouselAssets,
    setCarouselAssets,
    carouselCurrentAsset,
    setCarouselCurrentAsset,
    croppedAssets,
    lockedAspectRatio
}) {
    const videoRef = useRef(null);
    const classes = useStyles();

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [cropBoxSize, setCropBoxSize] = useState({ width: 0, height: 0 });
    const [videoSrcInternal, setVideoSrcInternal] = useState(videoSrc || "");
    const [baseAspect, setBaseAspect] = useState(aspectRatios[0]);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [trimmedVideoSrc, setTrimmedVideoSrc] = useState(null);
    const computedAspect = getComputedAspect(baseAspect, allowedOrientations[0]);
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(0);

    useEffect(() => {
        setVideoSrcInternal(videoSrc || "");
    }, [videoSrc]);

    const handleCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleCropSizeChange = useCallback(({ width, height }) => {
        setCropBoxSize(currentSize => (
            currentSize.width === width && currentSize.height === height
                ? currentSize
                : { width, height }
        ));
    }, []);


    /**
     * Called when user clicks Save on the header to finalize cropping
     * Performs final crop (and trim) of the video and passes the cropped file back to parent
     */
    const handleSave = async () => {
        try {
            const crop = {
                croppedAreaPixels: croppedAreaPixels || null,
                zoom: zoom,
            };
            const res = await fetch(videoSrcInternal);
            const aspectLabelForDB = baseAspect.standardName;
            const blob = await res.blob();

            const file = new File([blob], "processed-video.mp4", { type: blob.type });
            onCropComplete(file, crop, trimStart, trimEnd, aspectLabelForDB);

            if (carouselAssets) return;
            onClose(false);
        } catch (error) {
            console.error("Cropping failed:", error);
            Toast.error("Error: Video cropping failed. Please try again.", "error");
        }
    };

    const handleCancel = () => {
        setVideoSrcInternal("");
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        onClose(true);
    };

    useEffect(() => {
        if (lockedAspectRatio) {
            const matchingAvailableAspectData = aspectRatios.find(data => data.standardName === lockedAspectRatio);
            if (matchingAvailableAspectData) {
                setBaseAspect(matchingAvailableAspectData);
            }
        }
    }, [lockedAspectRatio]);

    return (
        <FlexContainer>
            <Dialog
                open={isOpen}
                maxWidth={false}
                onClose={handleCancel}
                PaperProps={{ className: classes.paper }}
            >
                <Header onCancel={handleCancel} onSave={handleSave} />
                <Container>
                    <Sidebar>
                        <div style={{
                            pointerEvents: lockedAspectRatio ? "none" : "all",
                            opacity: lockedAspectRatio ? 0.5 : 1
                        }}>
                            <AspectRatioSelector
                                aspectRatios={aspectRatios}
                                baseAspect={baseAspect}
                                setBaseAspect={setBaseAspect}
                            />
                        </div>

                        {carouselAssets &&
                            <AssetList
                                carouselAssets={carouselAssets}
                                setCarouselAssets={setCarouselAssets}
                                carouselCurrentAsset={carouselCurrentAsset}
                                setCarouselCurrentAsset={setCarouselCurrentAsset}
                                croppedAssets={croppedAssets}
                            />}
                    </Sidebar>
                    <CropperSection
                        videoSrcInternal={videoSrcInternal}
                        crop={crop}
                        zoom={zoom}
                        setCrop={setCrop}
                        setZoom={setZoom}
                        computedAspect={computedAspect}
                        handleCropComplete={handleCropComplete}
                        handleCropSizeChange={handleCropSizeChange}
                        cropBoxSize={cropBoxSize}
                        setVideoSrcInternal={setVideoSrcInternal}
                        videoRef={videoRef}
                        trimmedVideoSrc={trimmedVideoSrc}
                        setTrimmedVideoSrc={setTrimmedVideoSrc}
                        trimStart={trimStart}
                        setTrimStart={setTrimStart}
                        trimEnd={trimEnd}
                        setTrimEnd={setTrimEnd}
                        setCropBoxSize={setCropBoxSize}
                    />
                </Container>
            </Dialog>
        </FlexContainer>
    );
}
